import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Equations and inequalities, Grade 7 into Grade 8.
 *
 * An equation stops being a puzzle with one hidden step and becomes a sentence
 * you rewrite. The organising idea in the first two lessons is *balance*:
 * whatever is done to one side is done to the other, and the reason that works
 * is the same reason a pair of scales works.
 *
 * The third goes further, and is the reason the file runs past Grade 7. Once
 * the unknown appears on both sides it can cancel, and a student who has only
 * ever been told to "solve for x" reads that as a failure. It is not: it is the
 * equation answering a question about *how many* numbers work, and the answer
 * is sometimes all of them and sometimes none.
 */
export const EQUATION_LESSONS: Lesson[] = [
  lesson({
    id: 'multi-step-equations-01',
    unitId: 'unit-equations',
    conceptId: 'multi-step-equations',
    title: 'Multi-Step Equations',
    summary: 'Solving when the unknown is behind more than one operation, or appears on both sides.',
    objectives: [
      'Solve equations of the form ax + b = c by undoing operations in reverse order',
      'Expand a bracket correctly before solving',
      'Collect variable terms when the unknown appears on both sides',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Undoing, in reverse',
        blocks: [
          text('You can already solve x + 7 = 12, and 3x = 21. Here is one that needs both moves:'),
          math('3x + 7 = 22'),
          text('Think about what happened to x on its way to the left-hand side. It was multiplied by 3, and *then* 7 was added. To get back to x you retrace that path backwards: take the 7 off first, then divide by 3.'),
          insight('Undo in the reverse of the order things were done — the same reason you take your shoes off before your socks.'),
        ],
      },
      intuition: {
        title: 'Balance is the whole rule',
        blocks: [
          text('An equation is a claim that two things weigh the same. Anything you do to one side you must do to the other, or the claim stops being true — and every legal step in algebra is just that idea applied.'),
          figure(
            { kind: 'tape-diagram', rows: [{ label: '3x', parts: 3 }, { label: 'x', parts: 1 }], unitLabel: 'each block is one x' },
            'Three x blocks weigh the same as 15, so one x weighs 5. Dividing both sides by 3 is that sentence in symbols.',
          ),
          table(['Step', 'Equation', 'What was done'], [
            ['', '3x + 7 = 22', 'the problem'],
            ['1', '3x = 15', 'subtracted 7 from both sides'],
            ['2', 'x = 5', 'divided both sides by 3'],
            ['check', '3(5) + 7 = 22', 'substituted back'],
          ]),
          warning('Dividing first is the most common wrong route: 22 ÷ 3 gives 7.33…, and the 7 has not been dealt with at all. The addition was done last, so it must be undone first.', 'Undoing out of order'),
          text('When there is a bracket, expand it — and expand *all* of it:'),
          math('4(x + 3) = 4x + 12, \\quad\\text{not}\\quad 4x + 3'),
          text('The 4 multiplies everything inside. Missing the second term is easy to do and easy to miss, because the result still looks like a sensible equation.'),
          text('When x appears on both sides, one extra move comes first: get all the x terms onto one side.'),
          math('5x + 2 = 2x + 17 \\;\\longrightarrow\\; 3x + 2 = 17'),
          insight('Subtracting 2x from both sides is legal for exactly the same reason subtracting a number is: both sides changed by the same amount. Move the smaller x term, and the coefficient you are left with stays positive.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Every equation in this lesson is solved by the same loop, whatever it looks like at the start:'),
          list([
            'Expand any brackets.',
            'Collect the variable terms on one side, the numbers on the other.',
            'Undo the operations in reverse order.',
            'Substitute your answer back in and check it.',
          ], true),
          tip('That last step is not padding. Substituting back catches essentially every sign error, and it takes ten seconds.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve 5(x − 2) = 3x + 8.'),
          steps([
            { label: 'Expand the bracket — the 5 multiplies both terms.', latex: '5x - 10 = 3x + 8' },
            { label: 'Subtract 3x from both sides to collect the unknowns.', latex: '2x - 10 = 8' },
            { label: 'Add 10 to both sides.', latex: '2x = 18' },
            { label: 'Divide both sides by 2.', latex: 'x = 9' },
            { label: 'Check: the left side.', latex: '5(9 - 2) = 5 \\times 7 = 35' },
            { label: 'Check: the right side.', latex: '3(9) + 8 = 35' },
          ]),
          text('Both sides come to 35, so the answer is right — and you know it is right without being told.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multi-step-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('multi-step-equations', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('multi-step-equations', 3, 'Brackets and unknowns on both sides together.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Whatever you do to one side, do to the other.',
            'Undo operations in the reverse of the order they were applied.',
            'A multiplier outside a bracket multiplies everything inside it.',
            'Collect the variable terms on one side before solving.',
            'Always substitute your answer back in.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'inequalities-01',
    unitId: 'unit-equations',
    conceptId: 'inequalities',
    title: 'Inequalities',
    summary: 'Statements about a range of numbers rather than a single value — and the one rule that reverses.',
    objectives: [
      'Read and write inequalities, including ≤ and ≥',
      'Solve inequalities using the same steps as equations',
      'Reverse the inequality when multiplying or dividing by a negative',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Answers that are ranges',
        blocks: [
          text('A lift can carry 600 kg. That is not a statement about one number — it is a statement about every load up to 600 kg.'),
          math('w \\le 600'),
          text('An **inequality** describes a range of acceptable values. Most real constraints are like this: budgets, speed limits, passing marks, deadlines. An equation is the unusual case, not the normal one.'),
        ],
      },
      intuition: {
        title: 'Almost identical to an equation — with one exception',
        blocks: [
          text('Solve an inequality exactly as you would an equation. Add, subtract, multiply and divide on both sides, and the statement stays true.'),
          math('2x + 5 < 17 \\;\\longrightarrow\\; 2x < 12 \\;\\longrightarrow\\; x < 6'),
          text('There is exactly one rule that differs, and it is worth seeing before being told. Start with something obviously true:'),
          math('2 < 5'),
          text('Now multiply both sides by −1. The left becomes −2, the right becomes −5. Is −2 still less than −5? No — on the number line −2 sits to the *right* of −5.'),
          math('-2 > -5'),
          insight('Multiplying or dividing by a negative reflects the number line, so the order of everything on it reverses. The inequality sign must turn round with it.'),
          warning('This applies only to multiplying and dividing by a negative. Adding or subtracting a negative changes nothing about the direction — −8 is still less than −3 after you add 100 to both.', 'When it does not apply'),
          tip('If the sign makes you nervous, avoid it: rearrange so the variable ends up with a positive coefficient. From −3x < 12, add 3x to both sides and subtract 12, giving 0 < 3x + 12, and no reversal is needed.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: -8, to: 8, step: 1, marks: [{ at: -5, label: '−5' }, { at: -2, label: '−2' }, { at: 2, label: '2' }, { at: 5, label: '5' }] },
            'On the right, 2 comes before 5. Reflected to the left, −2 comes after −5. That reflection is the whole rule.',
          ),
          table(['Symbol', 'Means', 'Boundary included?'], [
            ['x < 6', 'less than 6', 'no — an open circle'],
            ['x ≤ 6', 'at most 6', 'yes — a filled circle'],
            ['x > 6', 'greater than 6', 'no'],
            ['x ≥ 6', 'at least 6', 'yes'],
          ]),
          text('"At most" and "at least" are the phrases you will meet in real problems, and they both include the boundary. A lift rated for 600 kg can carry 600 kg.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve −4x + 9 ≥ 25.'),
          steps([
            { label: 'Subtract 9 from both sides. No reversal — this is subtraction.', latex: '-4x \\ge 16' },
            { label: 'Divide both sides by −4. This is where it reverses.', latex: 'x \\le -4' },
            { label: 'Check with a value in the range, say x = −5.', latex: '-4(-5) + 9 = 29 \\ge 25 \\;\\checkmark' },
            { label: 'Check with a value outside it, say x = 0.', latex: '-4(0) + 9 = 9 \\ge 25 \\;\\times' },
          ]),
          text('Testing one value from inside the range and one from outside confirms both the boundary and the direction. It is the fastest way to catch a sign that should have flipped.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('inequalities', 4)] },
      independent: { title: 'On your own', blocks: [practice('inequalities', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('inequalities', 3, 'Negative coefficients, where the direction is the point.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An inequality describes a range of values, not a single one.',
            'Solve it exactly like an equation.',
            'Multiplying or dividing by a negative reverses the inequality sign.',
            '≤ and ≥ include the boundary value; < and > do not.',
            'Test a value inside your range and one outside it.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'linear-equation-solutions-01',
    unitId: 'unit-linear-equations',
    conceptId: 'linear-equation-solutions',
    title: 'One Solution, None, or Infinitely Many',
    summary: 'What it means when the unknown disappears — and why that is an answer rather than a failure.',
    objectives: [
      'Solve equations with the variable on both sides',
      'Recognise when an equation is true for every number, and when it is true for none',
      'Decide how many solutions an equation has without solving it in full',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'When x cancels out',
        blocks: [
          text('Every equation you have solved so far had one answer waiting at the end. Solve this one:'),
          math('2x + 5 = 2x + 9'),
          text('Take 2x from both sides — which is allowed, and which you have done a hundred times — and you are left with 5 = 9. There is no x anywhere, and what remains is simply false.'),
          insight('That is not a mistake, and it is not a dead end. It is the answer. No number makes 5 equal 9, so no number solves the equation: it has **no solution**.'),
        ],
      },
      intuition: {
        title: 'Three things can happen, and only three',
        blocks: [
          text('An equation with the unknown on both sides is a race between two linear rules. There are exactly three outcomes, and you can see all of them as graphs of two lines.'),
          table(['What is left after tidying', 'Meaning', 'How many solutions'], [
            ['x = 4', 'one number works', 'exactly one'],
            ['7 = 9', 'never true', 'none — the lines are parallel'],
            ['7 = 7', 'always true', 'infinitely many — one line, drawn twice'],
          ]),
          text('The middle row is 2x + 5 = 2x + 9: two lines climbing at the same rate from different starting points, so they never meet. The last row is what happens when both sides say the same thing in different words:'),
          math('3(2x - 4) = 6x - 12'),
          text('Expand the left side and it *is* the right side. Every number is a solution, because the sentence was never a condition on x at all — it was an identity.'),
          warning('An equation with no solution is not the same as an equation with the solution zero. Test it: 2(0) + 5 = 5 and 2(0) + 9 = 9, so x = 0 fails like every other number. "No solution" means the answer box has nothing to hold, not that it holds a nought.', 'No solution is not x = 0'),
          tip('You can often see which case you are in before doing any work. Compare the coefficients of x once both sides are expanded: different coefficients give exactly one solution; equal coefficients give none or infinitely many, and the constants decide which.'),
        ],
      },
      visual: {
        title: 'The same three cases as lines',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 8,
              lines: [
                { slope: 2, intercept: 1, label: 'y = 2x + 1' },
                { slope: 2, intercept: 5, label: 'y = 2x + 5', dashed: true },
              ],
            },
            'Equal slopes, different intercepts: the lines never cross, so 2x + 1 = 2x + 5 has no solution.',
          ),
          text('Change one slope and the lines cross exactly once — one solution. Make the two rules identical and the second line lands on top of the first, meeting it everywhere — infinitely many.'),
          insight('This is the same picture as a system of equations, seen from one variable instead of two. The number of solutions of an equation is the number of times two lines meet.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve 5(x − 2) = 5x + 3, and then 4(x + 1) = 2x + 10.'),
          steps([
            { label: 'Expand the first equation.', latex: '5x - 10 = 5x + 3' },
            { label: 'Subtract 5x from both sides.', latex: '-10 = 3' },
            { label: 'What is left is false, so nothing can satisfy it.', latex: '\\text{no solution}' },
            { label: 'Now expand the second.', latex: '4x + 4 = 2x + 10' },
            { label: 'Subtract 2x, then 4.', latex: '2x = 6' },
            { label: 'Divide.', latex: 'x = 3' },
          ]),
          text('The two look almost identical on the page, and the difference is entirely in the coefficients of x: 5 against 5 in the first, 4 against 2 in the second. That is the thing to look at first.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-equation-solutions', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-equation-solutions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('linear-equation-solutions', 3, 'Choosing a coefficient to force a particular number of solutions.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Collect the variable on one side; whatever is left tells you which case you are in.',
            'A false statement like 5 = 9 means no solution.',
            'A true statement like 7 = 7 means every number is a solution.',
            'No solution is not x = 0 — zero fails too.',
            'Equal coefficients of x mean the lines are parallel; different ones mean they cross exactly once.',
          ]),
        ],
      },
    },
  }),
];
